import { when, Lambda } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { Routes } from '../../_controllers/RouteController';
import { ApplicationStore } from '../../_stores/ApplicationStore';
import { NameButtonIcon, SupportButtonIcon, TightKnitLogoWhite } from '../../images/images';
import { fullContainer } from '../../styles/styleObjects';
import { CloseButton } from '../CloseButton';
import { CommonActionButton } from '../CommonActionButton';
import { InformationIcon } from '../InformationIcon';
import { AccountSettingsDropdown, SupportDropdown } from './AccountSettingsDropdown';
import { styles } from './NavigatorStyles';
import { NotificationsButton } from './NotificationsButton';
import { NotificationsDropdown } from './NotificationsDropDown';

import { LearningCenter } from '../../_modals/LearningCenter/LearningCenter';

import './MainPageWithNavigator.css';

interface INavButtonProps {
    text: string;
    action(): void;
}

interface ICloseButtonProps {
    dark?: boolean;
    action(): void;
}

interface IProps {
    store?: ApplicationStore;
    activeTabState: ActiveTabState;
    title?: React.ReactNode;
    titleInfoIconHref?: string,
    actionButtonAreaChildren?: React.ReactNode;
    primaryButton?: INavButtonProps;
    secondaryButton?: INavButtonProps;
    closeButton?: ICloseButtonProps;
    middleTabs?: {
        state: number;
        render(state: number): JSX.Element;
    };
    subTitle?: React.ReactNode;
}

interface IState {
    userInitials: string;
    showNotificationPopOver: boolean;
    showAccountPopover: boolean;
    showSupportPopover: boolean;
    showLearningCenter: boolean;
    learningCenterItemId: number;
}

export enum ActiveTabState {
    NONE = 'Notifications',
    ALBUMS = 'Albums',
    PHOTOS = 'Photos',
    FAMILIES = 'Families',
    FAMILY_DETAIL = 'Family Detail',
    PROFILE_SETTINGS = 'Profile Settings',
    STORY_DETAIL = 'Story Detail'
}

@inject('store')
@observer
class MainPageWithNavigator extends React.Component<IProps & RouteComponentProps<any>, IState> {
    private authListenerDisposer: Lambda = () => null;

    public state: IState = {
        userInitials: '',
        showNotificationPopOver: false,
        showAccountPopover: false,
        showSupportPopover: false,
        showLearningCenter: false,
        learningCenterItemId: 0,
    };

    public componentDidMount(): void {

        if (this.props.location.state && this.props.location.state.showLearningCenter) {
            this.setState({showLearningCenter: true});
        }

        this.authListenerDisposer = when(
            () => !!this.props.store && this.props.store.authorizationStore.authIsResolved && !this.props.store.authorizationStore.isAuthenticated,
            () => {
                this.props.history.push(Routes.LOGIN);
            }
        );

        when(
            () => !!this.props.store && this.props.store.authorizationStore.authIsResolved && !!this.props.store.userStore,
            () => {
                const store = this.props.store!.userStore;
                this.setState({ userInitials: store.getUserInitials() });
            }
        );
    }

    public componentWillUnmount(): void {
        this.authListenerDisposer();
    }

    private onClickNavigationTab = (forState: ActiveTabState): void => {
        let route = Routes.ALBUMS;
        switch (forState) {
            case ActiveTabState.ALBUMS:
                route = Routes.ALBUMS;
                break;
            case ActiveTabState.PHOTOS:
                route = Routes.PHOTOS;
                break;
            case ActiveTabState.FAMILIES:
                route = Routes.FAMILIES;
                break;
            case ActiveTabState.FAMILY_DETAIL:
            case ActiveTabState.PROFILE_SETTINGS:
            case ActiveTabState.STORY_DETAIL:
                break;
            default:
                console.assert(false, 'Case unhandled in @onClickNavigationTabb in <MainPageWithNavigator />');
        }

        this.props.history.push(route);
    }

    private renderNotificationAndUserSettingsButtons() {
        return (
            <div style={styles.navButtonsContainer}>
                <img
                    style={styles.navButtonIcon}
                    src={SupportButtonIcon}
                    onClick={() => this.setState({ showSupportPopover: !this.state.showSupportPopover, showAccountPopover: false, showNotificationPopOver: false })}
                />
                <NotificationsButton
                    onClick={() => this.setState({ showNotificationPopOver: !this.state.showNotificationPopOver, showAccountPopover: false, showSupportPopover: false })}
                />
                <div
                    style={styles.iconContainer}
                    onClick={() => this.setState({ showAccountPopover: !this.state.showAccountPopover, showNotificationPopOver: false, showSupportPopover: false })}
                >
                    <img src={NameButtonIcon} style={styles.navButtonIcon} />
                    <p style={styles.nameText}>{this.state.userInitials}</p>
                </div>
                {this.state.showSupportPopover &&
                <SupportDropdown
                    showItem={this.showLearningCenterModal}
                    hideLearningCenter={this.hideLearningCenterModal}
                    {...this.props}
                />
                }
                {this.state.showAccountPopover && <AccountSettingsDropdown {...this.props} />}
                {this.state.showNotificationPopOver && <NotificationsDropdown />}
            </div>
        );
    }

    private renderNavigationTabs(state: ActiveTabState) {
        //TODO: Fix fonts... font weight isn't coming through

        const NavigationTab = (props: { forState: ActiveTabState }) => {
            const isActive = this.props.activeTabState === props.forState;

            return (
                <div className="unselectable" style={styles.tabContainer} onClick={() => this.onClickNavigationTab(props.forState)}>
                    <p style={styles.navTabText(isActive)}>{props.forState}</p>
                    {isActive && <div style={styles.activeTabIndicator} />}
                </div>
            );
        };

        return (
            <div style={styles.innerNavBar}>
                <div style={styles.navigationTabContainer}>
                    <NavigationTab forState={ActiveTabState.ALBUMS} />
                    <NavigationTab forState={ActiveTabState.PHOTOS} />
                    <NavigationTab forState={ActiveTabState.FAMILIES} />
                </div>
                <div style={styles.flexOne} />
            </div>
        );
    }

    private renderActionButton() {
        if (this.props.primaryButton === undefined) {
            return null;
        }

        let buttonText = this.props.primaryButton.text;
        let onClick = this.props.primaryButton.action;

        return <CommonActionButton buttonContent={buttonText} onClick={onClick} containerStyle={styles.buttonContainer} fontStyle={styles.buttonText} />;
    }

    private renderSecondActionButton() {
        if (this.props.secondaryButton === undefined) {
            return null;
        }

        let buttonText = this.props.secondaryButton.text;
        let onClick = this.props.secondaryButton.action;

        return (
            <CommonActionButton
                buttonContent={buttonText}
                onClick={onClick}
                containerStyle={{ ...styles.buttonContainer, ...{ backgroundColor: '#CBE6E4' } }}
                fontStyle={{ ...styles.buttonText, ...{ color: '#00A59B' } }}
            />
        );
    }

    private renderCloseButton() {
        if (this.props.closeButton === undefined) {
            return null;
        }

        let onClick = this.props.closeButton.action;
        let dark = this.props.closeButton.dark;

        return (
            <CloseButton dark={dark} onClick={onClick} />
        );
    }

    public showLearningCenterModal = (itemId: number) => {
        console.log('showLearningCenterModal');
        console.log(itemId);
        this.setState({ showLearningCenter: true });
        this.setState({ learningCenterItemId: itemId });
        this.setState({ showSupportPopover: false });
    }

    public hideLearningCenterModal = () => {
        console.log('hide LC modal');
        this.setState({ showLearningCenter: false });
    }

    public render() {
        return (
            <div style={{ ...fullContainer }}>
                {this.state.showLearningCenter &&
                <LearningCenter
                    show={this.state.showLearningCenter}
                    handleClose={this.hideLearningCenterModal}
                    itemId={this.state.learningCenterItemId}
                />
                }

                <div style={styles.mainNavBarContainer}>
                    <img className="unselectable" src={TightKnitLogoWhite} style={styles.logoImage} onClick={() => this.props.history.push(Routes.ALBUMS)}/>
                    {this.renderNavigationTabs(this.props.activeTabState)}
                    {this.renderNotificationAndUserSettingsButtons()}
                </div>

                <div style={styles.subNavBarContainer}>
                    <div className="titleBar__headerText unselectable" style={styles.headerText}>
                        {this.props.title ? <React.Fragment>
                                {this.props.title}
                                {this.props.titleInfoIconHref &&
                                    <InformationIcon link={this.props.titleInfoIconHref}
                                                     style={styles.titleInfoIcon}/>}
                            </React.Fragment>
                            : this.props.activeTabState}

                    </div>
                    {this.props.subTitle && (
                        <div className="unselectable" style={styles.subHeaderText}>
                            {this.props.subTitle}
                        </div>
                    )}

                    {this.props.middleTabs && this.props.middleTabs.render(this.props.middleTabs.state)}
                    <div style={styles.flex}>
                        {this.props.actionButtonAreaChildren ?
                            this.props.actionButtonAreaChildren
                        :
                            <React.Fragment>
                                {this.renderSecondActionButton()}
                                {this.renderActionButton()}
                                {this.renderCloseButton()}
                            </React.Fragment>
                        }
                    </div>
                </div>
                {(this.state.showNotificationPopOver || this.state.showAccountPopover || this.state.showSupportPopover) && (
                    <div
                        style={styles.notificationPopoverContainer}
                        onClick={() => this.setState({ showNotificationPopOver: false, showAccountPopover: false, showSupportPopover: false })}
                    />
                )}
                <div style={{ height: 200 }} />
                <div style={styles.body}>{this.props.children}</div>
            </div>
        );
    }
}

export const NavigationContainer = withRouter<IProps & RouteComponentProps<any>>(MainPageWithNavigator);



// WEBPACK FOOTER //
// ./src/SharedComponents/MainPageWithNavigator/MainPageWithNavigator.tsx